<template>
  <div class="wizard-sections-selector flex flex-col h-full w-full px-6">
    <div class="selector-header mb-4">
      <h3 class="text-lg font-medium" :style="{ color: themeVars.text }">
        {{ selectorTitle }}
      </h3>
      <p class="text-sm" :style="{ color: themeVars.text2 }">
        {{ selectorSubtitle }}
      </p>
    </div>

    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <NSpin :color="themeVars.primary" size="large" />
    </div>

    <div v-else-if="!sections.length" class="flex-1 flex items-center justify-center">
      <p :style="{ color: themeVars.text2 }">
        {{ t('canvasUI.completionWizard.noSectionsAvailable') }}
      </p>
    </div>

    <div v-else class="flex-1 overflow-hidden">
      <Draggable
        v-model="sections"
        class="grid gap-3 overflow-y-auto max-h-[60vh] pr-2"
        :options="{ handle: '.section-drag-handle', animation: 150, touchStartThreshold: 10 }"
        style="grid-template-columns: repeat(auto-fill, minmax(250px, 1fr))"
        tag="div"
      >
        <div
          v-for="s in sections"
          :key="s.id"
          class="section-item bg-primary6 p-3 rounded-md relative w-[250px] min-h-[120px]"
          :class="{
            'border-2 border-primary': isSelected(s.id),
            'opacity-70': !isSelected(s.id),
          }"
        >
          <!-- Top controls bar with drag handle and checkbox -->
          <div class="flex justify-between items-center mb-3">
            <div class="section-drag-handle cursor-move z-10" :class="{ invisible: !isSelected(s.id) }">
              <CIcon :color="themeVars.primary" fa-type="fas" icon="grip-vertical" />
            </div>

            <NCheckbox :checked="isSelected(s.id)" @update:checked="toggleSelection(s)" />
          </div>

          <!-- Image centered -->
          <div class="section-thumbnail mb-3 flex justify-center">
            <CImage
              :ai-img-description="s.name"
              class="rounded-md overflow-hidden"
              :height="120"
              lazy
              object-fit="cover"
              :src="s.thumbnail_url ?? s.metadata?.thumbnail"
              width="100%"
            />
          </div>

          <!-- Content information centered -->
          <div class="section-info text-center">
            <div class="section-name font-medium break-words" :style="{ color: themeVars.text }">
              {{ s.name }} and more things that are really long
            </div>
            <div
              v-if="s.metadata?.pptx_data?.slides?.[0]?.content"
              class="section-description text-xs break-words"
              :style="{ color: themeVars.text2 }"
              v-html="s.metadata.pptx_data.slides[0].content"
            />
          </div>
        </div>
      </Draggable>
    </div>

    <div class="selector-footer mt-4 flex justify-between">
      <div class="selection-info" :style="{ color: themeVars.text2 }">
        {{ t('canvasUI.completionWizard.selectedCount', { count: selectedSectionIds.length, total: sections.length }) }}
      </div>
      <div class="flex gap-2">
        <CButton :disabled="!isValid" type="primary" @click="confirm">
          {{ t('canvasUI.common.ok') }}
        </CButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSpin, NCheckbox } from 'naive-ui'
import { VueDraggable as Draggable } from 'vue-draggable-plus'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { urlSafeFetchInChunks } from '@lib/util/network'
import type { CanvasRetrieve } from '@openapi'
import type { CanvasBuilderProps, SectionListProps } from '@canvas-builder/types/canvas'

const props = defineProps<{
  preselectedIds: string[]
  selectionStrategy: 'at-least-one' | 'single'
  sectionListData: SectionListProps
}>()

const emit = defineEmits<{
  (e: 'confirm', selectedIds: string[]): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const listCanvases = inject<CanvasBuilderProps['listCanvases']>('listCanvases')

const loading = ref(false)
const sections = ref<CanvasRetrieve[]>([])
const selectedSectionIds = ref<string[]>([])

const selectorTitle = computed(() => {
  if (props.selectionStrategy === 'single') {
    return t('canvasUI.completionWizard.selectExactlyOneSection')
  }
  return t('canvasUI.completionWizard.selectAtLeastOneSection')
})

const selectorSubtitle = computed(() => {
  if (props.selectionStrategy === 'single') {
    return t('canvasUI.completionWizard.mustSelectOneSection')
  }
  return t('canvasUI.completionWizard.selectAtLeastOne')
})

const isValid = computed(() => {
  if (props.selectionStrategy === 'single') {
    return selectedSectionIds.value.length === 1
  }
  return selectedSectionIds.value.length > 0
})

async function fetchSections() {
  if (!listCanvases) return

  loading.value = true
  try {
    if (props.sectionListData?.sections?.length) {
      // Get the IDs from the sections array
      const restrictedIds = props.sectionListData.sections.map((s) => s.id)

      // Use urlSafeFetchInChunks to fetch preselected sections
      const preselectedSections = await urlSafeFetchInChunks(
        restrictedIds,
        async (chunk) => {
          const response = await listCanvases?.({
            is_section: true,
            is_template: false,
            ordering: 'name',
            id__in: chunk.join(','),
          })
          return response?.results || []
        },
        { chunkSize: 50 },
      )

      sections.value = preselectedSections
    }
  } catch (error) {
    console.error('Failed to fetch sections for wizard step', error)
  } finally {
    loading.value = false
  }
}

function isSelected(id: string) {
  return selectedSectionIds.value.includes(id)
}

function toggleSelection(section: CanvasRetrieve) {
  const sectionId = section.id

  if (selectedSectionIds.value.includes(sectionId)) {
    selectedSectionIds.value = selectedSectionIds.value.filter((id) => id !== sectionId)
  } else {
    // Add to selection
    if (props.selectionStrategy === 'single') {
      selectedSectionIds.value = [sectionId]
    } else {
      selectedSectionIds.value.push(sectionId)
    }
  }
}

function updateSelectedSections() {
  // Re-order the selected sections based on their current order
  const newOrder = sections.value
    .filter((section) => selectedSectionIds.value.includes(section.id))
    .map((section) => section.id)

  selectedSectionIds.value = newOrder
}

function confirm() {
  if (isValid.value) {
    emit('confirm', selectedSectionIds.value)
  }
}

onMounted(async () => {
  // Fetch sections first
  await fetchSections()

  if (props.preselectedIds.length && props.selectionStrategy === 'at-least-one') {
    selectedSectionIds.value = [...props.preselectedIds]
  }

  // Add event listener for when drag ends to update selected sections order
  const draggableEl = document.querySelector('.section-drag-handle')
  if (draggableEl) {
    draggableEl.addEventListener('mouseup', updateSelectedSections)
  }
})
</script>
